<template>
  <SingleCardLayout>
    <!-- Verifying Code State -->
    <LoadingSpinner
      v-if="isVerifyingCode"
      title="Signing you in..."
      subtitle="Please wait while we verify your code."
    />

    <!-- Regular Login State -->
    <div v-else>
      <!-- Company Logo Display -->
      <div class="has-text-centered mb-5">
        <img
          :src="currentLogo"
          :alt="currentCompany?.name || 'Vysual'"
          class="login-logo"
          @error="handleLogoError"
        />
      </div>

      <div class="has-text-centered">
        <div class="title is-4 mt-5 has-text-weight-normal mb-2">
          {{ formTitle }}
        </div>
        <p class="subtitle is-6 has-text-grey mb-5">{{ formSubtext }}</p>
      </div>

      <!-- Error notification -->
      <div class="notification is-danger" v-if="loginError" role="alert">
        {{ loginError }}
      </div>

      <!-- Step 1: Email Entry -->
      <form v-if="!codeSent" @submit.prevent="onSubmitEmail">
        <div class="field">
          <label class="label" for="floatingInput">Email address</label>
          <input
            type="email"
            class="input"
            :class="{ 'is-danger': loginError }"
            id="floatingInput"
            placeholder="jane.doe@example.com"
            v-model="credentials.email"
          />
        </div>
        <div class="field" v-if="loginWithPass">
          <input
            type="password"
            class="input"
            id="floatingPassword"
            placeholder="Password"
            v-model="credentials.password"
          />
          <label class="label" for="floatingPassword">Password</label>
        </div>
        <div class="level mt-4">
          <button
            type="submit"
            class="button is-primary is-fullwidth"
            id="loginbtn"
            :class="{ 'is-loading': isSubmitting }"
          >
            LOGIN
          </button>
        </div>
      </form>

      <!-- OAuth Buttons (visible on email step only) -->
      <div v-if="!codeSent && !loginWithPass" class="oauth-section">
        <div class="divider-with-text">
          <span>or</span>
        </div>
        <div class="oauth-buttons">
          <button
            class="button oauth-btn oauth-google"
            :class="{ 'is-loading': isGoogleLoading }"
            :disabled="isSubmitting"
            @click="onGoogleSignIn"
          >
            <span class="icon">
              <img src="/assets/img/google-icon.svg" alt="" width="18" height="18" />
            </span>
            <span>Sign in with Google</span>
          </button>
          <button
            class="button oauth-btn oauth-microsoft"
            :class="{ 'is-loading': isMicrosoftLoading }"
            :disabled="isSubmitting"
            @click="onMicrosoftSignIn"
          >
            <span class="icon">
              <img src="/assets/img/microsoft-icon.svg" alt="" width="18" height="18" />
            </span>
            <span>Sign in with Microsoft</span>
          </button>
        </div>
      </div>

      <!-- Step 2: Code Entry -->
      <div v-else>
        <form @submit.prevent="onSubmitCode">
          <CodeInput
            ref="codeInputRef"
            v-model="codeString"
            :has-error="!!loginError"
            @complete="onSubmitCode"
          />
          <button
            type="submit"
            class="button is-primary is-fullwidth mt-5"
            :class="{ 'is-loading': isSubmitting }"
            :disabled="codeString.length !== 6"
          >
            VERIFY
          </button>
        </form>
        <div class="has-text-centered mt-4">
          <button
            class="button is-text has-text-grey"
            :disabled="resendCooldown > 0"
            @click="onResendCode"
          >
            {{ resendCooldown > 0 ? `Resend code (${resendCooldown}s)` : 'Resend code' }}
          </button>
          <span class="mx-2 has-text-grey-light">|</span>
          <button
            class="button is-text has-text-grey"
            @click="onBackToEmail"
          >
            Use different email
          </button>
        </div>
      </div>
    </div>
  </SingleCardLayout>
</template>

<script setup>
import { reactive, computed, onMounted, onUnmounted, ref, watch } from "vue";
import { useUserAuthStore } from "@/stores/userAuthStore";
import { useRouter } from "vue-router";
import SingleCardLayout from "@/layouts/SingleCard.vue";
import LoadingSpinner from "@/components/global/LoadingSpinner.vue";
import { companyCache } from "@/services/companyCache";
import { logger } from "@/services/loggerService";
import { defaultUserRoute } from '@/utils/userRouting';
import CodeInput from "@/components/common/CodeInput.vue";
import { useBunnyUrl } from "@/composables/useBunnyUrl";
import { BUNNY_URL_INTENT } from "@/utils/bunnyConstants";

const userAuthStore = useUserAuthStore();
const router = useRouter();
const loginWithPass = ref(false);
const currentCompany = ref(null);

// Code entry state
const codeInputRef = ref(null);
const codeString = ref('');
const resendCooldown = ref(0);
let cooldownInterval = null;

// Define the default logo path
const defaultLogoPath = "/assets/img/vysual.png";

// Watch for successful authentication and redirect appropriately
watch(() => userAuthStore.user.id, (newUserId) => {
  if (newUserId) {
    // Clear any problematic redirect URLs first
    const savedRedirectUrl = localStorage.getItem('redirectUrl');
    if (savedRedirectUrl && savedRedirectUrl.includes('/nexus/')) {
      logger.warn('[Login] Clearing admin redirect URL for non-admin user', {
        savedUrl: savedRedirectUrl,
        userType: userAuthStore.user.type
      });
      localStorage.removeItem('redirectUrl');
    }

    // User is now authenticated, redirect based on their type and companies
    const user = userAuthStore.user;
    logger.info('[Login] User authenticated, determining redirect', {
      userId: user.id,
      userType: user.type,
      companies: user.companies?.length || 0,
      activeCompanyId: user.activeCompanyId
    });

    // Shared with the router's guards so a fresh sign-in and a redirect agree —
    // agency staff land in their portal either way.
    const destination = defaultUserRoute(user);
    if (destination) {
      router.push(destination);
    } else {
      logger.warn('[Login] User has no companies', { userId: user.id });
      router.push({ name: "login", query: { error: 'no-companies' } });
    }
  }
});

// Extract company ID from URL if user was redirected from a company route
const getCompanyIdFromRedirectUrl = () => {
  try {
    const redirectUrl = localStorage.getItem("redirectUrl");
    if (redirectUrl) {
      const companyId = companyCache.extractCompanyIdFromPath(redirectUrl);
      if (companyId) {
        return companyId;
      }
    }

    const currentPath = window.location.pathname;
    const currentCompanyId = companyCache.extractCompanyIdFromPath(currentPath);
    if (currentCompanyId) {
      return currentCompanyId;
    }
  } catch (error) {
    logger.debug("[Login] Failed to parse redirect URL for company ID", {
      error: error.message,
    });
  }
  return null;
};

// Load company data if available
const loadCompanyContext = () => {
  let companyId = getCompanyIdFromRedirectUrl();

  if (!companyId) {
    const recentCompany = companyCache.getMostRecentCompany();
    if (recentCompany) {
      companyId = recentCompany.id;
      logger.info("[Login] Using most recently accessed company", {
        companyId: recentCompany.id,
        companyName: recentCompany.name,
      });
    }
  }

  if (companyId) {
    logger.info("[Login] Detected company context", { companyId });

    const cachedCompany = companyCache.getCompany(companyId);
    if (cachedCompany) {
      currentCompany.value = cachedCompany;
      companyCache.setAndApplyTheme(companyId, cachedCompany);
      logger.info("[Login] Applied company branding from cache", {
        companyId,
        companyName: cachedCompany.name,
        hasLogo: !!cachedCompany.logoUrl,
      });
    } else {
      logger.debug("[Login] No cached company data found", { companyId });
    }
  } else {
    const cacheStats = companyCache.getCacheStats();
    logger.debug("[Login] No company context found", {
      cacheStats,
      allCachedCompanies: companyCache.getAllCachedCompanies().map((c) => ({
        id: c.id,
        name: c.name,
      })),
    });
  }
};

// Bunny logo URLs are signed with a ~1h TTL — the stored company.logoUrl
// 403s once it expires. Re-mint a fresh signed URL at render time from
// logoAssetId; legacy Firebase logos (no assetId) fall through to logoUrl.
const { url: resolvedLogoUrl, refresh: refreshLogoUrl } = useBunnyUrl(() => ({
  assetId: currentCompany.value?.logoAssetId,
  fallback: currentCompany.value?.logoUrl,
  intent: BUNNY_URL_INTENT.THUMBNAIL,
}));

// Computed property for current logo
const currentLogo = computed(() => resolvedLogoUrl.value || defaultLogoPath);

// Handle logo loading errors
let logoRetried = false;
watch(() => currentCompany.value?.id, () => { logoRetried = false; });
const handleLogoError = async (error) => {
  const imgElement = error.target;
  const logoUrl = imgElement?.src;

  // First failure with a re-mintable Bunny asset: token likely expired.
  if (!logoRetried && currentCompany.value?.logoAssetId) {
    logoRetried = true;
    await refreshLogoUrl();
    return;
  }

  logger.warn("[Login] Logo load failed, using default logo", {
    originalSrc: logoUrl,
    isSvg: logoUrl?.toLowerCase().includes('.svg'),
    companyId: currentCompany.value?.id,
    companyName: currentCompany.value?.name,
    errorType: error?.type,
    errorMessage: error?.message
  });

  if (imgElement && imgElement.src !== defaultLogoPath) {
    imgElement.src = defaultLogoPath;
  }
};

onMounted(() => {
  window.addEventListener("keydown", handleKeyPress);
  loadCompanyContext();
});

onUnmounted(() => {
  window.removeEventListener("keydown", handleKeyPress);
  clearCooldownTimer();

  if (currentCompany.value) {
    companyCache.resetTheme();
    logger.debug("[Login] Reset theme on unmount");
  }
});

const credentials = reactive({
  email: "",
  password: "",
});

// Computed properties to access store values in template
const codeSent = computed(() => userAuthStore.codeSent);
const loginError = computed(() => userAuthStore.authError);
const isSubmitting = computed(() => userAuthStore.isSubmitting);
const isVerifyingCode = computed(() => userAuthStore.isVerifyingCode);

// OAuth loading states
const isGoogleLoading = ref(false);
const isMicrosoftLoading = ref(false);

const formTitle = computed(() => {
  if (codeSent.value) {
    return "Check your email";
  }
  return "";
});

const formSubtext = computed(() => {
  if (codeSent.value) {
    return `We sent a verification code to ${userAuthStore.codeSentEmail}`;
  }
  return "Login to start creating and managing your assets with vysual!";
});

// --- Email submission ---
const onSubmitEmail = async () => {
  if (!credentials.email) {
    userAuthStore.authError = "Please enter the email address associated with your account.";
    return;
  }

  if (loginWithPass.value) {
    try {
      await userAuthStore.loginWithEmail(credentials.email, credentials.password);
    } catch {
      // Error handled in store
    }
  } else {
    try {
      const { inviteToken } = await userAuthStore.sendAuthCode(credentials.email) || {};
      if (inviteToken) {
        router.push({ name: 'invite-accept', params: { token: inviteToken } });
        return;
      }
      startResendCooldown();
    } catch {
      // Error handled in store
    }
  }
};

// --- Code submission ---
const onSubmitCode = async () => {
  const code = codeString.value;
  if (code.length !== 6) return;

  try {
    await userAuthStore.verifyAuthCode(userAuthStore.codeSentEmail, code);
  } catch {
    // Error handled in store — clear the code input for retry
    codeInputRef.value?.clear();
  }
};

// --- Resend cooldown ---
const startResendCooldown = () => {
  resendCooldown.value = 30;
  clearCooldownTimer();
  cooldownInterval = setInterval(() => {
    resendCooldown.value--;
    if (resendCooldown.value <= 0) {
      clearCooldownTimer();
    }
  }, 1000);
};

const clearCooldownTimer = () => {
  if (cooldownInterval) {
    clearInterval(cooldownInterval);
    cooldownInterval = null;
  }
};

const onResendCode = async () => {
  if (resendCooldown.value > 0) return;
  userAuthStore.authError = "";

  try {
    await userAuthStore.sendAuthCode(userAuthStore.codeSentEmail);
    startResendCooldown();
    // Clear any previously entered digits
    codeInputRef.value?.clear();
  } catch {
    // Error handled in store
  }
};

const onBackToEmail = () => {
  userAuthStore.resetCodeState();
  codeString.value = '';
};

// --- OAuth sign-in ---
const onGoogleSignIn = async () => {
  isGoogleLoading.value = true;
  try {
    await userAuthStore.signInWithGoogle();
  } catch {
    // Error handled in store
  } finally {
    isGoogleLoading.value = false;
  }
};

const onMicrosoftSignIn = async () => {
  isMicrosoftLoading.value = true;
  try {
    await userAuthStore.signInWithMicrosoft();
  } catch {
    // Error handled in store
  } finally {
    isMicrosoftLoading.value = false;
  }
};

// --- Keyboard shortcuts ---
const handleKeyPress = (event) => {
  if (event.shiftKey && event.key === "P") {
    loginWithPass.value = true;
  }
};

// Check if URL parameter exists and set loginWithPass ref accordingly
const urlParams = new URLSearchParams(window.location.search);
if (urlParams.get("loginWithPass") === "true") {
  loginWithPass.value = true;
}
</script>

<style lang="scss" scoped>
.row {
  div[class*="col-"] {
    padding-inline: 0;
  }
}

.login-logo {
  max-height: 80px;
  max-width: 200px;
  width: auto;
  height: auto;
  object-fit: contain;
  transition: opacity 0.3s ease;

  &:hover {
    opacity: 0.8;
  }
}

.oauth-section {
  margin-top: 16px;
}

.divider-with-text {
  display: flex;
  align-items: center;
  margin-bottom: 16px;

  &::before,
  &::after {
    content: '';
    flex: 1;
    border-bottom: 1px solid #dbdbdb;
  }

  span {
    padding: 0 12px;
    color: #7a7a7a;
    font-size: 0.85rem;
    text-transform: lowercase;
  }
}

.oauth-buttons {
  display: flex;
  gap: 10px;
}

.oauth-btn {
  flex: 1;
  font-weight: 500;
  font-size: 0.9rem;
  border-radius: 6px;
  height: 42px;
  transition: all 0.15s ease;

  .icon img {
    display: block;
  }
}

.oauth-btn.oauth-google.button {
  background-color: #fff !important;
  border-color: #dadce0 !important;
  color: #3c4043 !important;

  &:hover {
    background-color: #f7f8f8 !important;
    border-color: #c6c6c6 !important;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  }
}

.oauth-btn.oauth-microsoft.button {
  background-color: #2f2f2f !important;
  border-color: #2f2f2f !important;
  color: #fff !important;

  &:hover {
    background-color: #1a1a1a !important;
    color: #fff !important;
  }

  &.is-loading::after {
    border-color: transparent transparent #fff #fff !important;
  }
}

</style>
